import { useLayoutEffect, useEffect, useState } from "react";

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { map, forEach } from "lodash";
import DOMPurify from 'dompurify';

import "./index.scss";

import dataFromDefaultJson from "./data.json";

gsap.registerPlugin(ScrollTrigger);

const VideoPlayOnScrollWithText = ({ data, idPrefix }) => {
  const [textContainerHeight, setTextContainerHeight] = useState(0);
  const [topValues, setTopValues] = useState([]);

  const { backgroundVideo, texts, delayFactor, posterImage } = data;

  // Added check for mobile or desktop and use the relevant video src accordingly
  const mobileDeviceWidth = 1024;

  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= mobileDeviceWidth
  );

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= mobileDeviceWidth);
    }
    // Attach the event listener
    window.addEventListener("resize", handleResize);

    // Use the Fetch API to download the video, make it into an objectURL, and attach it to the video element.
    /* const videoElement = document.querySelector(`#edint-video-scroll-play-video-${idPrefix}`);
    if (videoElement && videoElement.firstChild) {
      console.log("called fetch")
      fetchVideo(videoElement.firstChild)
    } else {
      console.log("not called")
    } */

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useLayoutEffect(() => {
    // lower numbers = faster playback (This signifies the scrollY px covered in 1 second of the video)
    const playbackConst = 180;

    // select video element
    const video = document.querySelector(
      `#edint-video-scroll-play-video-${idPrefix}`
    );

    function once(el, event, fn, opts) {
      var onceFn = function (e) {
        el.removeEventListener(event, onceFn);
        fn.apply(this, arguments);
      };
      el.addEventListener(event, onceFn, opts);
      return onceFn;
    }

    // For IOS devices effectively - for video to be activated
    once(document.documentElement, "touchstart", function (e) {
      video.play();
      video.pause();
    });

    // Play video from current duration
    once(video, "loadedmetadata", () => {
      // GSAP Animation
      let tl = gsap.timeline({
        scrollTrigger: {
          trigger: `#edint-video-scroll-play-app-container-${idPrefix}`,
          start: "top top",
          end: "bottom bottom",
          scrub: true,
          ease: "power1",
        },
      });

      // dynamically set the video+text container page height according to video length
      setTextContainerHeight("1800vh");

      // play video on scroll
      tl.fromTo(
        video,
        {
          currentTime: 0 + Number(delayFactor),
        },
        {
          currentTime: video.duration - Number(delayFactor) || 1,
        }
      );

      // calculate text heights based on duration (Adjust with distanceAdjuster value)
      const textsPositions = [];
      forEach(texts, (text, index) => {
        let duration = texts[index]?.duration || 0,
          distance = texts[index]?.distanceAdjuster || 1;
        duration = Number(duration) + Number(delayFactor);
        textsPositions.push(
          Math.floor(duration) * playbackConst * distance + "px"
        );
      });
      setTopValues(textsPositions);
    });
  }, []);

  return (
    <div
      className="edint-video-scroll-play-app-container"
      data-videoclass={idPrefix}
      id={`edint-video-scroll-play-app-container-${idPrefix}`}
    >
      <link
        rel="preload"
        href={isMobile ? backgroundVideo.mobileSrc : backgroundVideo.src}
        as="video"
        type="video/mp4"
        fetchpriority="high"
      ></link>
      <div className="media video" data-videoclass={idPrefix}>
        <video
          id={`edint-video-scroll-play-video-${idPrefix}`}
          data-videoclass={idPrefix}
          className="edint-video-scroll-play-video"
          tabIndex="0"
          muted="muted"
          preload={isMobile ? "metadata" : "auto"}
          poster={isMobile ? posterImage.mobileSrc : posterImage.src}
          playsInline
          webkit-playsinline="true"
        >
          <source
            type="video/mp4"
            src={isMobile ? backgroundVideo.mobileSrc : backgroundVideo.src}
          />
          {backgroundVideo.text}
        </video>
      </div>

      <div
        className="edint-video-scroll-play-texts"
        id={`edint-video-scroll-play-texts-${idPrefix}`}
        data-videoclass={idPrefix}
        style={{ height: textContainerHeight }}
      >
        {map(texts, (textContent, index) => {
          return (
            <div
              className="edint-video-scroll-play-textbox"
              id={`edint-video-scroll-play-textbox-${index}`}
              data-videoclass={idPrefix}
              style={{ top: `${topValues[index]}` }}
              key={index}
            >
              <p dangerouslySetInnerHTML = { {__html: DOMPurify.sanitize(textContent?.text) } }/>
            </div>
          );
        })}
      </div>
    </div>
  );
};

VideoPlayOnScrollWithText.defaultProps = {
  data: dataFromDefaultJson,
  idPrefix: "temp",
};

export default VideoPlayOnScrollWithText;
